/**
 * Tâche 1134 (Eric 2026-06-09) — Le champ de recherche du Dictionnaire
 * profite des mêmes équivalences anglaises que la recherche globale.
 *
 * Taper « TFSA » dans la page devait remonter « CELI ». Le filtrage est
 * client-side (toutes les entrées sont déjà chargées) : on élargit donc la
 * requête à ses équivalents connus au lieu d'appeler le serveur.
 */

import { foldAccents } from "./textFold"; // Tâche 1165

/** Même normalisation que `dedup_key` côté backend : casse, accents,
 *  séparateurs et pluriel ignorés. */
export function normalizeKey(text) {
  const stripped = foldAccents(text)
    .replace(/\([^)]*\)/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  if (!stripped) return "";
  const invariable = new Set(["fonds", "taux", "choix", "cours", "corps", "temps",
    "prix", "mois", "pays", "avis", "cas", "sens", "dos", "sous", "revenus"]);
  return stripped
    .split(" ")
    .map((w) => {
      if (invariable.has(w) || w.length <= 3) return w;
      if (w.endsWith("aux") && w.length > 4) return `${w.slice(0, -3)}al`;
      if (w.endsWith("eaux")) return w.slice(0, -1);
      if (w.endsWith("eux") && w.length > 4) return w.slice(0, -1);
      if (w.endsWith("s") && !w.endsWith("ss")) return w.slice(0, -1);
      return w;
    })
    .join(" ");
}

/**
 * Termes à chercher pour une requête donnée : la requête elle-même, plus ses
 * équivalents dans l'autre langue. « TFSA » → ["tfsa", "celi"].
 */
export function expandQuery(query, pairs = []) {
  const raw = String(query || "").trim();
  if (!raw) return [];
  const terms = [raw.toLowerCase()];
  const key = normalizeKey(raw);
  if (!key) return terms;
  for (const pair of pairs) {
    const fr = String(pair?.fr || "");
    const en = String(pair?.en || "");
    if (!fr || !en) continue;
    if (normalizeKey(fr) === key) terms.push(en.toLowerCase());
    else if (normalizeKey(en) === key) terms.push(fr.toLowerCase());
  }
  return [...new Set(terms)];
}

/** L'entrée correspond-elle à l'un des termes élargis ? */
export function entryMatchesQuery(entry, query, pairs = []) {
  const terms = expandQuery(query, pairs);
  if (terms.length === 0) return true;
  // Tâche 1165 (Eric 2026-06-17) — Accents repliés DES DEUX CÔTÉS : taper
  // « interet » doit trouver « intérêt », et taper « intérêt » doit trouver
  // une fiche saisie sans accent. Une recherche qui dépend d'un accent bien
  // placé n'est pas une recherche.
  const haystacks = [
    foldAccents(entry?.term),
    foldAccents(entry?.definition),
    ...(entry?.aliases || []).map((a) => foldAccents(a)),
  ];
  return terms.some((t) => {
    const needle = foldAccents(t);
    return needle && haystacks.some((h) => h.includes(needle));
  });
}
